import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconBell, IconBellRinging, IconLoader2 } from '@tabler/icons-react';
import axios from '../../library/axios';
import { CPANEL_FEED_REFRESH_EVENT } from '../../helpers/cpanelJobFeedback';
import {
  NOTIFICATION_TABS,
  formatNotificationTime,
} from '../../helpers/cpanelNotifications';

const POLL_INTERVAL_MS = 5000;

const CpanelNotifications = () => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = useCallback(
    async (tab = activeTab, options = {}) => {
      const { showLoading = false } = options;
      if (showLoading) {
        setLoading(true);
      }
      try {
        const { data } = await axios.get('/cpanel/notifications/list', {
          params: { tab, limit: 10 },
        });
        setUnreadCount(data?.unreadCount ?? 0);
        setNotifications(Array.isArray(data?.notifications) ? data.notifications : []);
      } catch {
        if (showLoading) {
          setUnreadCount(0);
          setNotifications([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [activeTab]
  );

  useEffect(() => {
    fetchNotifications(activeTab, { showLoading: false });
    const timer = window.setInterval(
      () => fetchNotifications(activeTab, { showLoading: false }),
      POLL_INTERVAL_MS
    );
    const onRefresh = () => fetchNotifications(activeTab, { showLoading: false });
    window.addEventListener(CPANEL_FEED_REFRESH_EVENT, onRefresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(CPANEL_FEED_REFRESH_EVENT, onRefresh);
    };
  }, [activeTab, fetchNotifications]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        fetchNotifications(activeTab, { showLoading: notifications.length === 0 });
      }
      return next;
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleMarkAllRead = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await axios.patch('/cpanel/notifications/mark-all-read');
      await fetchNotifications(activeTab);
    } catch {
      // ignore
    }
  };

  const handleNotificationClick = async (event, item) => {
    event.preventDefault();
    event.stopPropagation();

    if (!item.isRead) {
      try {
        await axios.patch(`/cpanel/notifications/${item.id}/mark-read`);
      } catch {
        // ignore
      }
    }

    setOpen(false);
    if (item.url) {
      navigate(item.url);
    }
  };

  const hasUnread = unreadCount > 0;

  return (
    <div className="cpanel-notifications" ref={rootRef}>
      <button
        type="button"
        className="cpanel-notifications__toggle"
        onClick={handleToggle}
        aria-label="Notifikasi"
        aria-expanded={open}
        aria-haspopup="true"
      >
        {hasUnread ? (
          <IconBellRinging size={22} stroke={1.75} className="cpanel-notifications__bell cpanel-notifications__bell--active" />
        ) : (
          <IconBell size={22} stroke={1.75} className="cpanel-notifications__bell" />
        )}
        {hasUnread ? (
          <span className="cpanel-notifications__dot" aria-hidden="true" />
        ) : null}
      </button>

      {open ? (
        <div className="cpanel-notifications__dropdown" role="menu">
          <div className="cpanel-notifications__header">
            <span className="cpanel-notifications__title">Notifikasi</span>
            {hasUnread ? (
              <button
                type="button"
                className="cpanel-notifications__mark-all"
                onClick={handleMarkAllRead}
              >
                Tandai semua dibaca
              </button>
            ) : null}
          </div>

          <div className="cpanel-notifications__tabs" role="tablist">
            {NOTIFICATION_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`cpanel-notifications__tab${activeTab === tab.id ? ' cpanel-notifications__tab--active' : ''}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleTabChange(tab.id);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="cpanel-notifications__list" role="tabpanel">
            {loading && notifications.length === 0 ? (
              <div className="cpanel-notifications__empty">Memuat...</div>
            ) : null}
            {!loading && notifications.length === 0 ? (
              <div className="cpanel-notifications__empty">Tidak ada notifikasi</div>
            ) : null}
            {notifications.map((item) => {
              const inProgress =
                item.type === 'job.started' || item.data?.status === 'running';
              const iconVariant =
                item.data?.icon === 'danger'
                  ? 'danger'
                  : item.data?.icon === 'progress' || inProgress
                    ? 'progress'
                    : 'success';

              return (
              <button
                key={item.id}
                type="button"
                className={`cpanel-notifications__item${!item.isRead ? ' cpanel-notifications__item--unread' : ''}${inProgress ? ' cpanel-notifications__item--progress' : ''}`}
                onClick={(event) => handleNotificationClick(event, item)}
              >
                <span
                  className={`cpanel-notifications__item-icon cpanel-notifications__item-icon--${iconVariant}`}
                  aria-hidden="true"
                >
                  {iconVariant === 'progress' ? (
                    <IconLoader2 size={16} stroke={1.75} className="cpanel-notifications__spin" />
                  ) : (
                    <IconBell size={16} stroke={1.75} />
                  )}
                </span>
                <span className="cpanel-notifications__item-body">
                  <span className="cpanel-notifications__item-title">{item.title}</span>
                  {item.body ? (
                    <span
                      className={`cpanel-notifications__item-text${item.data?.icon === 'danger' ? ' cpanel-notifications__item-text--error' : ''}`}
                    >
                      {item.body}
                    </span>
                  ) : null}
                  <span className="cpanel-notifications__item-time">
                    {formatNotificationTime(item.createdAt)}
                  </span>
                </span>
                {!item.isRead ? (
                  <span className="cpanel-notifications__item-dot" aria-hidden="true" />
                ) : null}
              </button>
            );
            })}
          </div>

          <div className="cpanel-notifications__footer">
            <Link
              to="/cpanel/notifications/all"
              className="cpanel-notifications__view-all"
              onClick={() => setOpen(false)}
            >
              Lihat semua notifikasi
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CpanelNotifications;
